import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import qs from "qs";
import { appConfig } from "../../config/app.config";
// Import the auth service to get the token
import authentication from '@feathersjs/authentication-client';
import { feathers } from '@feathersjs/feathers';
import rest from '@feathersjs/rest-client';
// DELETED - court-research feature removed
// import { CourtResearchQueryApiPayload, FetchFullTextPayload, FullTextApiResponse } from '../../redux/thunks/AICourtResearchThunks';
import authService from "../../services/auth.service";


export interface UpdateManyRequest<T> {
    cmd: string;
    entities: T[];
    service: string;
}

export type UpdateManyResponse<T> = T[];
export type CreateManyResponse<T> = T[];


export interface Paginated<T> {
    total: number;
    limit: number;
    skip: number;
    data: T[];
}
export interface FindParams {
    service: string;
    query?: any;
}

export interface GetParams {
    service: string;
    _id: string;
}

export interface CreateParams {
    service: string;
    entity: any;
}

export interface CreateManyParams {
    service: string;
    entities: any;
}


export interface ProcessDocumentParams {
    caseId: string;
    knowledgebaseId: string;
    documentName: string;
    documentType: 'text' | 'pdf' | 'word' | 'html';
    documentContent?: string;
    googleDriveToken?: string;
    googleFileId?: string;
    documentDescription?: string;
    metadata?: {
        mimeType?: string;
        author?: string;
        createdAt?: string;
        tags?: string[];
        category?: string;
    };
}

export interface ProcessDocumentResponse {
    success: boolean;
    documentId: string;
    processingStatus: 'completed' | 'processing' | 'error';
    metadata?: any;
    entriesCount: number;
}


//console.log(appConfig)

export const tagTypes = [
    "user-messages",
    "templates",
    "lists",
    "contacts",
    "labels",
    "tasks-extra-data",
    "tasks",
    "task-groups",
    "boards",
    "message-log",
    "campaigns",
    "legal-queries",
];

export type ApiTagTypes = typeof tagTypes[number];

export const apiBaseQuery = fetchBaseQuery({
    baseUrl: appConfig.API_ENDPOINT,
    prepareHeaders: (headers: Headers) => {
        // Get the token from the auth service
        const token = authService.getToken();

        // If we have a token, add it to the Authorization header
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        return headers;
    },
});
export type ApiBaseQuery = typeof apiBaseQuery;

// Correct Feathers Client Initialization (should be a single instance for the app)
const app = feathers();
const apiUrl = appConfig.API_ENDPOINT; // Use the same config as RTK Query
const restClient = rest(apiUrl);
app.configure(restClient.fetch(window.fetch.bind(window)));
app.configure(authentication({
    storage: window.localStorage,
    storageKey: 'feathers-jwt',
    path: '/authentication',
    jwtStrategy: 'jwt',
}));



// Define a service using a base query and expected endpoints
export const backendApi = createApi({
    reducerPath: "backendApi",
    tagTypes,
    keepUnusedDataFor: 1,
    baseQuery: apiBaseQuery,
    endpoints: (builder) => ({
        findEntity: builder.query<Paginated<any>, FindParams>({
            query: ({ service, query }) => {
                let url = service;
                if (query) {
                    url += `/?${qs.stringify(query)}`;
                }
                return {
                    url,
                    method: "GET",
                };
            },
        }),

        getTotalCount: builder.query<number, { service: string }>({
            query: ({ service }) => {
                let url = service;
                const query = { $limit: 0 }; // Only fetch the total count
                url += `/?${qs.stringify(query)}`;
                return {
                    url,
                    method: "GET",
                };
            },
            transformResponse: (response: { total: number }) => response.total,
        }),

        createEntity: builder.mutation<any, CreateParams>({
            query: ({ service, entity }) => ({
                url: `${service}`,
                method: "POST",
                body: entity,
            }),
        }),

        patchEntity: builder.mutation<any, CreateParams>({
            query: ({ service, entity }) => ({
                url: `${service}/${entity._id}`,
                method: "PATCH",
                body: entity,
            }),
        }),

        updateManyEntities: builder.mutation<
            UpdateManyResponse<any>,
            CreateManyParams
        >({
            query: ({ service, entities }) => ({
                url: "actions",
                method: "POST",
                body: {
                    cmd: "update-many-entities",
                    service,
                    entities,
                },
            }),
        }),

        createManyEntities: builder.mutation<
            CreateManyResponse<any>,
            CreateManyParams
        >({
            query: ({ service, entities }) => ({
                url: "actions",
                method: "POST",
                body: {
                    cmd: "create-many-entities",
                    service,
                    entities,
                },
            }),
        }),

        deleteEntity: builder.mutation<any, CreateParams>({
            query: ({ service, entity }) => ({
                url: `${service}/${entity._id}`,
                method: "DELETE",
                body: entity,
            }),
        }),

        sendLegalQuery: builder.mutation({
            query: (params) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'legal-query-api-request',
                    queryText: params.queryText,
                    caseId: params.caseId,
                    chatId: params.chatId,
                    knowledgebaseId: params.knowledgebaseId,
                    queryId: params.queryId,
                    jurisdiction: params.jurisdiction,
                    language: params.language
                },
            }),
            invalidatesTags: ['legal-queries', 'ai-legal-messages'], // DELETED: 'ai-legal-citations'
        }),

        // DELETED - court-research feature removed
        // sendCourtResearchQuery: builder.mutation<any, CourtResearchQueryApiPayload>({ ... }),
        // getFullCourtRulingText: builder.mutation<FullTextApiResponse, FetchFullTextPayload>({ ... }),

        processDocument: builder.mutation<ProcessDocumentResponse, ProcessDocumentParams>({
            query: (params) => ({
                url: "actions",
                method: "POST",
                body: {
                    cmd: "knowledge-base.processDocument",
                    ...params
                },
            }),
        }),

        getScraperQueueUrlByDocumentId: builder.mutation<{ success: boolean; result?: { url?: string }; error?: string; code?: number; timestamp?: string }, { documentId: string }>({
            query: ({ documentId }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'get-scraper-queue-url-by-document-id',
                    documentId: documentId
                },
            }),
        }),

        proxyDocumentUrl: builder.mutation<{ result: { success: boolean; content?: string; contentType?: string; url?: string; error?: string; code?: number; timestamp?: string, source?: string; encoding?: string } }, { documentId: string }>({
            query: ({ documentId }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'proxy-document-url',
                    documentId: documentId
                },
            }),
        }),

        uploadProfilePicture: builder.mutation<{ success: boolean; publicUrl?: string; error?: string }, { imageData: string }>({
            query: (params) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'upload-profile-picture',
                    imageData: params.imageData
                },
            }),
        }),

        fetchRelevantDocuments: builder.mutation<any, { documentIds: string[] }>({
            query: ({ documentIds }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'fetch-relevant-documents',
                    documentIds: documentIds
                },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    console.log('[Backend API] fetchRelevantDocuments FULL RESPONSE:', JSON.stringify(data, null, 2));
                    console.log('[Backend API] data.result:', data?.result);
                    console.log('[Backend API] data.result.documents:', data?.result?.documents);
                    console.log('[Backend API] data.result.knowledgebases:', data?.result?.knowledgebases);

                    // Import reducer actions dynamically to avoid circular dependency
                    const { DocumentReducerActions, KnowledgebaseReducerActions } = await import('../../redux/thunks/DBServiceThunks');

                    // Automatically upsert documents to Redux state
                    // Response structure: { result: { success: true, documents: [...], knowledgebases: [...] } }
                    if (data?.result?.documents && Array.isArray(data.result.documents)) {
                        dispatch(DocumentReducerActions.upsertMany(data.result.documents));
                        console.log('[Backend API] Documents automatically upserted to Redux state', data.result.documents.length);
                    } else if (data && Array.isArray(data.documents)) {
                        // Fallback for old response format
                        dispatch(DocumentReducerActions.upsertMany(data.documents));
                        console.log('[Backend API] Documents automatically upserted to Redux state (fallback)');
                    }

                    // Automatically upsert knowledgebases to Redux state
                    if (data?.result?.knowledgebases && Array.isArray(data.result.knowledgebases)) {
                        dispatch(KnowledgebaseReducerActions.upsertMany(data.result.knowledgebases));
                        console.log('[Backend API] Knowledgebases automatically upserted to Redux state', data.result.knowledgebases.length);
                    } else {
                        console.warn('[Backend API] No knowledgebases found in response. Full data:', data);
                    }
                } catch (error) {
                    console.error('[Backend API] Failed to upsert documents/knowledgebases:', error);
                }
            },
        }),

        // Stripe subscription endpoints
        getCustomerSubscriptions: builder.mutation<any, void>({
            query: () => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'get-customer-subscriptions'
                },
            }),
        }),

        getAvailablePlans: builder.mutation<any, void>({
            query: () => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'get-available-plans'
                },
            }),
        }),

        createPaymentLink: builder.mutation<any, { priceId: string; successUrl: string; cancelUrl: string }>({
            query: ({ priceId, successUrl, cancelUrl }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'create-payment-link',
                    priceId,
                    successUrl,
                    cancelUrl
                },
            }),
        }),

        getCheckoutSession: builder.mutation<any, { sessionId: string }>({
            query: ({ sessionId }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'get-checkout-session',
                    sessionId
                },
            }),
        }),

        createCustomerPortalSession: builder.mutation<any, { returnUrl: string }>({
            query: ({ returnUrl }) => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'create-customer-portal-session',
                    returnUrl
                },
            }),
        }),

        createCustomerSession: builder.mutation<any, void>({
            query: () => ({
                url: 'actions',
                method: 'POST',
                body: {
                    cmd: 'create-customer-session'
                },
            }),
        }),

    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    // export mutations here
    useFindEntityQuery,
    useGetTotalCountQuery,
    useCreateEntityMutation,
    usePatchEntityMutation,
    useUpdateManyEntitiesMutation,
    useCreateManyEntitiesMutation,
    useDeleteEntityMutation,
    useSendLegalQueryMutation,
    // DELETED: useSendCourtResearchQueryMutation,
    useProcessDocumentMutation,
    // DELETED: useGetFullCourtRulingTextMutation,
    useGetScraperQueueUrlByDocumentIdMutation,
    useProxyDocumentUrlMutation,
    useFetchRelevantDocumentsMutation,
    useGetCustomerSubscriptionsMutation,
    useGetAvailablePlansMutation,
    useCreatePaymentLinkMutation,
    useGetCheckoutSessionMutation,
    useCreateCustomerPortalSessionMutation,
    useCreateCustomerSessionMutation,
} = backendApi;
export const backendApiUseMutations = backendApi;

export const {
    findEntity,
    getTotalCount,
    createEntity,
    patchEntity,
    updateManyEntities,
    createManyEntities,
    deleteEntity,
    sendLegalQuery,
    // DELETED: sendCourtResearchQuery,
    processDocument,
    // DELETED: getFullCourtRulingText,
    uploadProfilePicture,
    getCustomerSubscriptions,
    getAvailablePlans,
    createPaymentLink,
    getCheckoutSession,
    createCustomerPortalSession,
    createCustomerSession,
} = backendApi.endpoints;
export const backendApiEndpoints = backendApi.endpoints;
export const middleware = backendApi.middleware;
export default backendApi.reducer;

// Assuming FullTextData and FetchFullTextPayload types are or will be defined 
// (e.g. in AICourtResearchThunks.ts or a shared types file and imported here)
// For the purpose of this edit, we define simplified argument type here.
export interface FetchFullTextApiArg {
    rulingId: string;
}

// Define the expected response structure for the new endpoint
export interface FullTextDataForResult {
    fullText: string;
    textSource: string;
    rulingId: string;
    entryMetadata?: any;
    parentDocumentMetadata?: any;
}
export interface GetFullTextServiceResponse {
    success: boolean;
    result: FullTextDataForResult;
    timestamp?: string;
}

// DELETED - court-research feature removed
// export const sendCourtResearchQueryAction = backendApi.endpoints.sendCourtResearchQuery;
// export const getFullCourtRulingTextAction = backendApi.endpoints.getFullCourtRulingText;
